import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { faImage, faList, faRecycle, faStar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { Table } from 'react-bootstrap';
import Tooltip from 'react-bootstrap/Tooltip';
import { useHistory } from 'react-router-dom';

import ActionsMenu from 'components/shared/ActionsMenu';
import { areYouSure, SwalToast } from 'lib/utils/toast';
import { backgroundGrey, grape, softPurple, softPurpleLight } from 'lib/css/colors';
import Container from 'components/shared/Container';
import CreateProductListModal from 'components/product_lists/CreateProductListModal';
import EmptyState from 'components/shared/EmptyState';
import { httpPut } from 'lib/networking/http';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import pointerOnHover from 'lib/css/pointerOnHover';
import {
  PRODUCT_LISTS,
  getURL,
  PRODUCT_LIST_ID,
  PRODUCT_LIST_PRODUCTS_COVERAGE_METRICS,
} from 'lib/networking/endpoints';
import useDelete from 'lib/hooks/useDelete';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function ProductListTable({
  productListType,
  searchString,
  showCreateListModal,
  setShowCreateListModal,
}) {
  const history = useHistory();
  const hasUserPermissions = useUserPermissions();

  const {
    data: { product_lists: productLists } = {},
    loading,
    error,
    refetch,
  } = useGet(PRODUCT_LISTS, {
    offset: 0,
    limit: 15,
    product_list_type: productListType,
    search_string: searchString,
  });

  const { postData: reCalculate, loading: calculating } = usePost(
    PRODUCT_LIST_PRODUCTS_COVERAGE_METRICS,
    () => SwalToast('success', 'Please check after few minutes'),
    error =>
      SwalToast(
        'error',
        `An error occurred while re calculating: ${error?.response?.data?.message}`,
      ),
  );

  const { deleteData: deleteProductList } = useDelete(
    PRODUCT_LIST_ID,
    false,
    () => {
      SwalToast('success', 'Deleted successfully');
      refetch();
    },
    () => SwalToast('error', 'An error occurred'),
  );

  const updateProductList = useCallback(
    (product_list_id, is_user_favorite) => {
      const updatedUrl = getURL(PRODUCT_LIST_ID, { product_list_id });
      httpPut(updatedUrl, { is_user_favorite }).then(() => {
        refetch && refetch();
      });
    },
    [refetch],
  );

  const formatPercent = (numerator, denominator) => {
    if (!denominator) return 'N/A';
    return (numerator / denominator).toLocaleString(undefined, { style: 'percent' });
  };

  const goToProductList = productListId => {
    history.push('/product-lists/' + productListId);
  };

  if (calculating || loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return null;
  }

  return (
    <>
      <Container>
        <Table className={css(styles.tableText)}>
          <thead className={css(styles.thead)}>
            <tr>
              <th>Name</th>
              <th>Active Products</th>
              <th>UPB Match %</th>
              <th>Att Score</th>
              <th>eCom Score</th>
              <th>Classification %</th>
              <th>Images %</th>
              <th>Created By</th>
              <th className={css(styles.actionHeader)} />
            </tr>
          </thead>
          <tbody className={css(styles.tbody)}>
            {(!productLists || productLists.length === 0) && (
              <tr>
                <td colSpan={10}>
                  <EmptyState
                    icon={faList}
                    title="No product lists found"
                    description={
                      'Try adjusting your search or create a new list ' + 'to get started.'
                    }
                  />
                </td>
              </tr>
            )}
            {productLists?.map(prodList => (
              <tr
                key={prodList.id}
                className={classNames(css(styles.clickableRow), pointerOnHover)}
                onClick={() => goToProductList(prodList.id)}
              >
                <td>
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                      <Tooltip id={`recalc-${prodList.id}`}>
                        Get real time metrics for this product list
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon
                      icon={faRecycle}
                      role="button"
                      tabIndex={0}
                      aria-label="Recalculate metrics"
                      className={classNames(css(styles.recycleIcon), pointerOnHover, 'mr-2')}
                      onClick={e => {
                        e.stopPropagation();
                        reCalculate({ product_list_id: prodList.id, re_calculate: true });
                      }}
                    />
                  </OverlayTrigger>
                  {prodList.name}
                  {prodList.is_private ? (
                    <span className={css(styles.privateBadge)}>Private</span>
                  ) : null}
                </td>
                <td>{prodList.active_products_count?.toLocaleString()}</td>
                <td>{formatPercent(prodList.upb_matched_count, prodList.products_count)}</td>
                <td>
                  {prodList.products_count !== 0 &&
                  prodList.scoring_average !== null &&
                  prodList.scoring_average !== undefined
                    ? `${prodList.scoring_average.toFixed(2)}%`
                    : 'N/A'}
                </td>
                <td>
                  {prodList.products_count !== 0 &&
                  prodList.ecom_score_average !== null &&
                  prodList.ecom_score_average !== undefined
                    ? `${prodList.ecom_score_average.toFixed(2)}%`
                    : 'N/A'}
                </td>
                <td>{formatPercent(prodList.classified_count, prodList.products_count)}</td>
                <td>
                  {formatPercent(prodList.has_propagated_image_count, prodList.products_count)}
                </td>
                <td>{prodList.created_by.name}</td>
                <td className={css(styles.actionCell)} onClick={e => e.stopPropagation()}>
                  <ActionsMenu
                    actions={[
                      {
                        label: prodList.is_user_favorite
                          ? 'Remove from Favorites'
                          : 'Add to Favorites',
                        icon: prodList.is_user_favorite ? faStar : faStarRegular,
                        onClick: () => updateProductList(prodList.id, !prodList.is_user_favorite),
                      },
                      {
                        label: 'Assign Thumbnails',
                        icon: faImage,
                        onClick: () =>
                          window.open(
                            `/assign-thumbnails/${prodList.id}/${prodList.name}`,
                            '_blank',
                          ),
                        hidden: !hasUserPermissions(
                          [
                            UserPermission.ASSIGN_THUMBNAILS,
                            UserPermission.REVIEW_ASSIGNED_THUMBNAILS,
                            UserPermission.REVIEW_AI_SUGGESTED_THUMBNAILS,
                          ],
                          false,
                        ),
                      },
                      {
                        label: 'Delete',
                        icon: faTrash,
                        onClick: () =>
                          areYouSure(
                            () => deleteProductList({ product_list_id: prodList.id }),
                            'Are you sure you want to delete this product list?',
                          ),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Container>
      <CreateProductListModal
        show={showCreateListModal}
        onHide={() => setShowCreateListModal(false)}
        refetch={refetch}
      />
    </>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  recycleIcon: {
    color: grape,
    ':hover': {
      opacity: 0.7,
    },
  },
  privateBadge: {
    marginLeft: 8,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: softPurpleLight,
    color: softPurple,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  actionHeader: {
    width: 40,
  },
  actionCell: {
    textAlign: 'center',
    width: 40,
  },
  clickableRow: {
    ':hover': {
      backgroundColor: softPurpleLight,
    },
    transition: 'background-color 0.15s ease',
  },
});

ProductListTable.propTypes = {
  productListType: PropTypes.string,
  searchString: PropTypes.string,
  showCreateListModal: PropTypes.bool,
  setShowCreateListModal: PropTypes.func,
};

export default ProductListTable;
